import { useAngularContext } from 'AngularContext';
import { useStudentDashboardContent } from 'StudentDashboardContent';
import {
    type StreamForDashboard,
    type StreamIguanaClass,
    type ContentAccessHelperClass,
    streamToIguanaAttrs,
    type Stream,
} from 'Lessons';
import { useMemo } from 'react';

// Eventually when we refactor these hooks to use RTKQuery, we can use the localePackId and localePackIds arguments
// to filter the results via `selectFromResult` a la:
//
// api.useGetStreamQuery(undefined, {
//  selectFromResult: ({ data }) => ({
//      stream: data?.find((stream) => stream.localePackId === localePackId),
//    }),
//  })
//
// See also: https://redux-toolkit.js.org/rtk-query/usage/queries#selecting-data-from-a-query-result
//
// When we do this, we should also think about if it makes sense to expose these hooks outside of the current User / learner context.
// We'll be removing the dependency on useStudentDashboardContent / useLearnerContentCache, which implicitly depend on the current User.
// We could theoretically moved to a world where we:
//  1. Have a `usePlaylists` hook to get the playlists to display on the user's dashboard,
//  2. Have a `usePlaylist` hook to get a single (active?) Playlist's information, which calls into
//     the `useStreams` hook to get the Playlist's Stream's information.
//
// This would put us in a world where we have more HTTP requests but where we're also querying and caching individual Playlists and Streams
// in RTKQuery, which makes sense because we don't need information about EVERY Stream while in the Playlist map in the dashboard. We only need
// information about the Playlists the user should see when viewing that UI. Then when they have an active Playlist or click into a Playlist to
// make it active, we can use `usePlaylist` and `useStreams`.
export function useStudentDashboardStream(localePackId: string) {
    return useStudentDashboardStreams([localePackId])[0];
}

/** The streams returned are in the same order as `localePackIds`. */
export function useStudentDashboardStreams(localePackIds: string[]) {
    const { lessonStreamsByLocalePackId } = useStudentDashboardContent();

    return useMemo(
        () =>
            localePackIds
                .map(localePackId => lessonStreamsByLocalePackId[localePackId])
                .filter((stream): stream is StreamForDashboard => !!stream),
        [localePackIds, lessonStreamsByLocalePackId],
    );
}

export const useLaunchableStreams = (localePackIds: string[]) => {
    const ContentAccessHelper = useAngularContext().get<ContentAccessHelperClass>('ContentAccessHelper');
    const StreamIguanaClass = useAngularContext().get<StreamIguanaClass>('Lesson.Stream');
    const streams = useStudentDashboardStreams(localePackIds);

    // NOTE: We don't want to cache these Streams because they were only created to work with
    // ContentAccessHelper below and are missing some of the attributes that are needed to be cached (e.g. progress).
    const iguanaStreams = useMemo(
        () =>
            streams.map(stream => {
                const cachedStream = StreamIguanaClass.getCachedForLocalePackId(stream.localePackId, false);
                if (cachedStream) return cachedStream;

                // This is dangerous. `stream` here is an instance of StreamForDashboard. When
                // we cast it to Stream, we are promising that it has properties that it does
                // not in fact have. Once convert canLaunch to not rely on iguana we will be
                // able to resolve this.
                return StreamIguanaClass.new(streamToIguanaAttrs(stream as unknown as Stream<false>));
            }),
        [streams, StreamIguanaClass],
    );
    return useMemo(
        () =>
            streams.filter(stream => {
                const iguanaStream = iguanaStreams.find(s => s.localePackId === stream.localePackId);
                if (!iguanaStream) {
                    throw new Error(`Stream not found in iguanaStreams`);
                }
                return ContentAccessHelper.canLaunch(iguanaStream);
            }),
        [streams, iguanaStreams, ContentAccessHelper],
    );
};
